import { useCallback, useMemo } from 'react';
import { useAuthStore } from '@/features/auth/store/authStore';
import type { Permission } from '@/lib/permissions';

export const usePermissions = () => {
  const user = useAuthStore((state) => state.user);
  const permissions: string[] = useMemo(() => user?.permissions || [], [user?.permissions]);

  const hasPermission = useCallback((permission: Permission) => {
    return permissions.includes(permission);
  }, [permissions]);

  const hasAllPermissions = useCallback((requiredPermissions: Permission[]) => {
    if (requiredPermissions.length === 0) return true;
    return requiredPermissions.every((p) => permissions.includes(p));
  }, [permissions]);

  const hasAnyPermission = useCallback((allowedPermissions: Permission[]) => {
    if (allowedPermissions.length === 0) return true;
    return allowedPermissions.some((p) => permissions.includes(p));
  }, [permissions]);

  return useMemo(() => ({
    permissions,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
  }), [permissions, hasPermission, hasAllPermissions, hasAnyPermission]);
};
